import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getUserAssessments } from '@/lib/db';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Assessment } from '@/lib/types';
import { Calendar, Filter, Download, Search, MessageCircle, Brain, Activity } from 'lucide-react';

export default function History() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'green' | 'orange' | 'red'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!currentUser) {
      navigate('/barometre/login');
      return;
    }

    const loadAssessments = async () => {
      try {
        if (currentUser.id) {
          const data = await getUserAssessments(currentUser.id);
          setAssessments(data);
        }
      } catch (error) {
        console.error('Error loading assessments:', error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de charger l'historique.",
        });
        navigate('/barometre/dashboard');
      } finally {
        setLoading(false);
      }
    };

    loadAssessments();
  }, [currentUser, navigate, toast]);

  const filteredAssessments = assessments
    .filter(assessment => filter === 'all' || assessment.zone === filter)
    .filter(assessment => 
      searchTerm === '' || 
      assessment.symptoms.some(symptom => 
        symptom.toLowerCase().includes(searchTerm.toLowerCase())
      ) ||
      assessment.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assessment.response.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const getZoneColor = (zone: string) => {
    switch (zone) {
      case 'green': return 'bg-green-100 text-green-800';
      case 'orange': return 'bg-orange-100 text-orange-800';
      case 'red': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getZoneText = (zone: string) => {
    switch (zone) {
      case 'green': return 'Zone verte';
      case 'orange': return 'Zone orange';
      case 'red': return 'Zone rouge';
      default: return 'Zone inconnue';
    }
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Zone', 'Énergie', 'Symptômes', 'Réponse', 'Notes'];
    const csvContent = [
      headers.join(','),
      ...filteredAssessments.map(assessment => [
        format(new Date(assessment.date), 'dd/MM/yyyy HH:mm'),
        assessment.zone,
        `${assessment.overallEnergy}%`,
        `"${assessment.symptoms.join(', ')}"`,
        `"${assessment.response}"`,
        `"${assessment.notes || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `historique_evaluations_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100">
      <div className="max-w-6xl mx-auto space-y-6">
        <Card className="backdrop-blur-sm bg-white/90">
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Brain className="w-6 h-6 text-purple-600" />
                  Historique des évaluations
                </h1>
                <p className="text-gray-600 mt-1">
                  Consultez l'évolution de votre état énergétique
                </p>
              </div>
              <Button
                onClick={exportToCSV}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Exporter
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Filtres et recherche */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Rechercher dans les symptômes, réponses ou notes..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value as any)}
                    className="border rounded-md px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="all">Toutes les zones</option>
                    <option value="green">Zone verte</option>
                    <option value="orange">Zone orange</option>
                    <option value="red">Zone rouge</option>
                  </select>
                </div>
              </div>

              {/* Liste des évaluations */}
              <div className="space-y-4">
                {filteredAssessments.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Aucune évaluation trouvée
                  </div>
                ) : (
                  filteredAssessments.map((assessment) => (
                    <Card key={assessment.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        {/* Header */}
                        <div className="flex flex-col sm:flex-row justify-between gap-4 mb-4">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-gray-500" />
                              <span className="text-sm text-gray-600">
                                {format(new Date(assessment.date), "d MMMM yyyy 'à' HH:mm", { locale: fr })}
                              </span>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-sm ${getZoneColor(assessment.zone)}`}>
                              {getZoneText(assessment.zone)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Activity className="w-4 h-4 text-purple-600" />
                            <span className="text-lg font-semibold text-purple-600">
                              {assessment.overallEnergy}% d'énergie
                            </span>
                          </div>
                        </div>

                        {/* Symptoms */}
                        <div className="mb-4">
                          <h3 className="text-sm font-medium text-gray-700 mb-2">
                            Symptômes ressentis :
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {assessment.symptoms.map((symptom, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 bg-purple-100 text-purple-800 rounded-md text-sm"
                              >
                                {symptom}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Response */}
                        <div className="mb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <MessageCircle className="w-4 h-4 text-purple-600" />
                            <h3 className="text-sm font-medium text-gray-700">
                              Réponse :
                            </h3>
                          </div>
                          <p className="text-gray-600 text-sm bg-purple-50 p-3 rounded-md">
                            {assessment.response}
                          </p>
                        </div>

                        {/* Notes */}
                        {assessment.notes && (
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">
                              Notes personnelles :
                            </h3>
                            <p className="text-gray-600 text-sm italic">
                              {assessment.notes}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}